import React, { useCallback, useMemo } from 'react'

import { getUserDisplayName } from '../../../apps/professional-app/fa/common/aifeatures/utils'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'

import { selectProcessDocumentContact } from '../../../store/processDocument/documentUsers'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import {
  resetDocumentSummary,
  selectDocumentLabel,
  selectDocumentSummaryExecutionLoading,
  selectDocumentSummaryResult,
  selectDocumentType,
  selectDocumentTypeCalculationLoading,
  setShowDocumentSummary,
} from '../../../store/processDocument/documentSummary'
import {
  resetProcessDocumentInfo,
  selectProcessDocumentFileInfo,
  selectProcessDocumentSelectedFileName,
  selectProcessDocumentUploadIsLoading,
} from '../../../store/processDocument/documentInfo'

import { DocumentType, FALLBACK_DOCUMENT_TYPE_ID } from '../../../models/DocumentType'

import { ProcessDocumentFileUploadProgress, ProgressItem } from '../ProcessDocumentFileUploadProgress/ProcessDocumentFileUploadProgress'
import { ProcessDocumentFileInput } from '../ProcessDocumentFileInput/ProcessDocumentFileInput'
import { useDocumentUpload, useSafeTimeout } from '../hooks'

import { useDocumentSummary } from './hooks'

import './DocumentSummary.scss'

interface DocumentSummaryFileUploadProps {
  trackingEventContext?: 'contact_details' | 'new_contact'
}

export const DocumentSummaryFileUpload: React.FC<DocumentSummaryFileUploadProps> = ({ trackingEventContext = 'contact_details' }) => {
  const dispatch = useAppDispatch()

  const { tracked } = useTracking()

  const selectedFileName = useAppSelector(selectProcessDocumentSelectedFileName)
  const contact = useAppSelector(selectProcessDocumentContact)

  const { uploadFile } = useDocumentUpload()
  const { calculateDocumentType, summarizeDocument: _summarizeDocument } = useDocumentSummary()
  const { setSafeTimeout, clearSafeTimeout } = useSafeTimeout()

  const summarizeDocument = tracked(
    _summarizeDocument,
    {
      eventName: TrackEvent.DOCUMENT_SUMMARY_EXECUTED,
      eventDetails: {
        source: 'manual_upload',
        context: trackingEventContext,
      },
    },
    { eventDetailsExtractor: (documentType: DocumentType) => ({ fallbackToGeneric: documentType.systemDocumentTypeId === FALLBACK_DOCUMENT_TYPE_ID }) },
  )

  const resetFileUpload = useCallback(() => {
    dispatch(resetProcessDocumentInfo())
    dispatch(resetDocumentSummary())
  }, [dispatch])

  const handleFileSelected = useCallback(
    async (file: File) => {
      clearSafeTimeout()

      const fileInfo = await uploadFile(file)
      if (!fileInfo) {
        resetFileUpload()
        return
      }

      const documentType = await calculateDocumentType(fileInfo)
      if (!documentType) {
        resetFileUpload()
        return
      }

      const documentSummary = await summarizeDocument(documentType, fileInfo)
      if (!documentSummary) {
        resetFileUpload()
        return
      }

      setSafeTimeout(() => dispatch(setShowDocumentSummary(true)), 500)
    },
    [calculateDocumentType, clearSafeTimeout, dispatch, resetFileUpload, setSafeTimeout, summarizeDocument, uploadFile],
  )

  if (!contact) {
    return null
  }

  return (
    <div className='h-100 w-100 d-flex align-items-center justify-content-center'>
      <div className='d-flex flex-column align-items-center zocks-gap-2 '>
        <div className='d-flex flex-column align-items-center zocks-gap-0.5 document-summary-file-upload-header'>
          <h3>Document Intelligence</h3>
          <span className='paragraph-medium text-center'>
            Upload documents for {getUserDisplayName(contact)}. AI Assistant will extract key insights and prepare updates for your CRM and financial systems.
          </span>
        </div>
        {!selectedFileName ? <ProcessDocumentFileInput onFileSelected={handleFileSelected} /> : <DocumentSummaryFileUploadProgress />}
      </div>
    </div>
  )
}

const DocumentSummaryFileUploadProgress = () => {
  const selectedFileName = useAppSelector(selectProcessDocumentSelectedFileName)
  const isUploading = useAppSelector(selectProcessDocumentUploadIsLoading)
  const fileInfo = useAppSelector(selectProcessDocumentFileInfo)

  const documentTypeCalculationLoading = useAppSelector(selectDocumentTypeCalculationLoading)
  const documentType = useAppSelector(selectDocumentType)
  const documentLabel = useAppSelector(selectDocumentLabel)

  const documentSummaryResult = useAppSelector(selectDocumentSummaryResult)
  const documentSummaryExecutionLoading = useAppSelector(selectDocumentSummaryExecutionLoading)

  const progressItems: ProgressItem[] = useMemo(() => {
    if (!selectedFileName) {
      return []
    }
    return [
      {
        isLoading: isUploading,
        isSuccess: !isUploading && !!fileInfo,
        label: fileInfo ? `Uploaded ${fileInfo.fileName}` : `Uploading ${selectedFileName}…`,
      },
      {
        isLoading: documentTypeCalculationLoading,
        isSuccess: !documentTypeCalculationLoading && !!documentType,
        label: documentType ? (
          documentType.systemDocumentTypeId === FALLBACK_DOCUMENT_TYPE_ID ? (
            <>
              Couldn't match document type → Using <strong>{documentType.name}</strong> as fallback
            </>
          ) : (
            <>
              Identified as <strong>{documentLabel}</strong> → Applying <strong>{documentType.name}</strong> template
            </>
          )
        ) : (
          'Analyzing document type…'
        ),
      },
      {
        isLoading: documentSummaryExecutionLoading,
        isSuccess: !documentSummaryExecutionLoading && !!documentSummaryResult,
        label: documentSummaryResult ? 'Summary ready to review and sync' : 'Extracting insights and creating summary…',
      },
    ]
  }, [
    documentLabel,
    documentSummaryExecutionLoading,
    documentSummaryResult,
    documentType,
    documentTypeCalculationLoading,
    fileInfo,
    isUploading,
    selectedFileName,
  ])

  return <ProcessDocumentFileUploadProgress items={progressItems} />
}
